/**
 * Kompilasi program per jenis shader + lokasi uniform-nya. Lokasi dipetakan ke
 * interface bernama (bukan Record generik) supaya `noUncheckedIndexedAccess`
 * tidak menyisipkan `| undefined` ke setiap pemakai.
 */

import {
	LIQUID_GLASS_FRAGMENT_SRC,
	LIQUID_GLASS_VERTEX_SRC,
	LIQUID_MORPH_FRAGMENT_SRC,
} from "./orb-webgl-shaders";
import type { WebglShaderKind } from "./orb-webgl-stage.types";
import { compileProgram } from "./webgl-gl-utils";

type Loc = WebGLUniformLocation | null;

export interface GlassLocations {
	uResolution: Loc;
	uViewportOrigin: Loc;
	uTime: Loc;
	uDistortion: Loc;
	uSpeed: Loc;
	uFlow: Loc;
	uSparkle: Loc;
	uColor0: Loc;
	uColor1: Loc;
	uColor2: Loc;
	uColor3: Loc;
	uBrightness: Loc;
}

export interface MorphLocations {
	uResolution: Loc;
	uViewportOrigin: Loc;
	uTime: Loc;
	uK: Loc;
	uTaper: Loc;
	uRot: Loc;
	uSteps: Loc;
	uMode: Loc;
	uBrightness: Loc;
	uLightTint: Loc;
	uLightTintA: Loc;
}

export type ProgramInfo =
	| { kind: "liquid-glass"; program: WebGLProgram; locations: GlassLocations }
	| { kind: "liquid-morph"; program: WebGLProgram; locations: MorphLocations };

/** Kompilasi dan petakan uniform; melempar bila shader gagal (pemanggil → fallback Canvas 2D). */
export function buildProgramInfo(
	gl: WebGL2RenderingContext,
	kind: WebglShaderKind,
): ProgramInfo {
	if (kind === "liquid-morph") {
		const program = compileProgram(
			gl,
			LIQUID_GLASS_VERTEX_SRC,
			LIQUID_MORPH_FRAGMENT_SRC,
		);
		const u = (name: string): Loc => gl.getUniformLocation(program, name);
		return {
			kind,
			program,
			locations: {
				uResolution: u("uResolution"),
				uViewportOrigin: u("uViewportOrigin"),
				uTime: u("uTime"),
				uK: u("uK"),
				uTaper: u("uTaper"),
				uRot: u("uRot"),
				uSteps: u("uSteps"),
				uMode: u("uMode"),
				uBrightness: u("uBrightness"),
				uLightTint: u("uLightTint"),
				uLightTintA: u("uLightTintA"),
			},
		};
	}
	const program = compileProgram(
		gl,
		LIQUID_GLASS_VERTEX_SRC,
		LIQUID_GLASS_FRAGMENT_SRC,
	);
	const u = (name: string): Loc => gl.getUniformLocation(program, name);
	return {
		kind,
		program,
		locations: {
			uResolution: u("uResolution"),
			uViewportOrigin: u("uViewportOrigin"),
			uTime: u("uTime"),
			uDistortion: u("uDistortion"),
			uSpeed: u("uSpeed"),
			uFlow: u("uFlow"),
			uSparkle: u("uSparkle"),
			uColor0: u("uColor0"),
			uColor1: u("uColor1"),
			uColor2: u("uColor2"),
			uColor3: u("uColor3"),
			uBrightness: u("uBrightness"),
		},
	};
}
